import { DevTool } from "@hookform/devtools";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Divider, Stack } from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { MutationError } from "components/UI/MutationError";
import { formatISO, parseISO } from "date-fns";
import { useChurchStore } from "features/churches";
import { cloneDeep } from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateKid, useKid, useUpdateKid } from "../api";
import { useMembersStore } from "../stores";
import { MemberDataManipulator } from "../utils";
import { createKidSchema } from "../validation";
import {
  KidAddressSection,
  KidFamilyInformationSection,
  KidPersonalProfileSection
} from "./sections";

export function KidForm({ isEditMode }) {
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const setKidData = useMembersStore(state => state.setKidData);

  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  const kidCreateMutation = useCreateKid();
  const kidUpdateMutation = useUpdateKid();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    reset
  } = useForm({
    defaultValues: {
      first_name: "",
      father_name: "",
      last_name: "",
      gender: "",
      nationality_id: "66",
      birth_country_id: "66",
      birth_region_id: "",
      birth_zone_id: "",
      birth_city_id: "",
      birth_state: "",
      birth_city: "",
      birth_zip_code: "",
      birth_street_address: "",
      birth_building_number: "",
      date_of_birth: null,
      mother_language: "",
      second_language: "",
      academic_status: "",
      status: "kid",
      is_primary: "0",
      primary_id: "",
      primary_first_name: "",
      primary_father_name: "",
      primary_last_name: "",
      primary_relationship: "",
      primary_religion: "",
      primary_phone: "",
      primary_attending_church: "",
      is_secondary: "0",
      secondary_id: "",
      secondary_first_name: "",
      secondary_father_name: "",
      secondary_last_name: "",
      secondary_relationship: "",
      secondary_religion: "",
      secondary_phone: "",
      secondary_attending_church: "",
      country_id: "66",
      region_id: "",
      zone_id: "",
      city_id: "",
      subcity_id: "",
      woreda_id: "",
      kebele: "",
      current_state: "",
      current_city: "",
      current_zip_code: "",
      current_street_address: "",
      current_building_number: "",
      house_number: "",
      specific_location: ""
    },
    resolver: yupResolver(createKidSchema)
  });

  const kidQuery = useKid({
    churchId: selectedChurch?.id,
    kidId: id,
    config: {
      enabled: !!id
    }
  });

  useEffect(() => {
    if (isEditMode && kidQuery.data) {
      const kidData = kidQuery.data.result;

      reset({
        first_name: kidData?.first_name ?? "",
        father_name: kidData?.father_name ?? "",
        last_name: kidData?.last_name ?? "",
        gender: kidData?.gender ?? "",
        nationality_id: kidData?.nationality_id
          ? String(kidData?.nationality_id)
          : "66",
        birth_country_id: kidData?.birth_country_id
          ? String(kidData?.birth_country_id)
          : "66",
        birth_region_id: kidData?.birth_region_id ?? "",
        birth_zone_id: kidData?.birth_zone_id ?? "",
        birth_city_id: kidData?.birth_city_id ?? "",
        birth_state: kidData?.birth_state ?? "",
        birth_city: kidData?.birth_city ?? "",
        birth_zip_code: kidData?.birth_zip_code ?? "",
        birth_street_address: kidData?.birth_street_address ?? "",
        birth_building_number: kidData?.birth_building_number ?? "",
        date_of_birth: kidData.date_of_birth
          ? parseISO(kidData.date_of_birth)
          : null,
        mother_language: kidData?.mother_language ?? "",
        second_language: kidData?.second_language ?? "",
        academic_status: kidData?.academic_status ?? "",
        status: kidData?.status ?? "kid",
        is_primary: kidData?.is_primary ? String(kidData?.is_primary) : "0",
        primary_id: kidData?.primary_id ?? "",
        primary_first_name: kidData?.primary_first_name ?? "",
        primary_father_name: kidData?.primary_father_name ?? "",
        primary_last_name: kidData?.primary_last_name ?? "",
        primary_relationship: kidData?.primary_relationship ?? "",
        primary_religion: kidData?.primary_religion ?? "",
        primary_phone: kidData?.primary_phone ?? "",
        primary_attending_church: kidData?.primary_attending_church ?? "",
        is_secondary: kidData?.is_secondary
          ? String(kidData?.is_secondary)
          : "0",
        secondary_id: kidData?.secondary_id ?? "",
        secondary_first_name: kidData?.secondary_first_name ?? "",
        secondary_father_name: kidData?.secondary_father_name ?? "",
        secondary_last_name: kidData?.secondary_last_name ?? "",
        secondary_relationship: kidData?.secondary_relationship ?? "",
        secondary_religion: kidData?.secondary_religion ?? "",
        secondary_phone: kidData?.secondary_phone ?? "",
        secondary_attending_church: kidData?.secondary_attending_church ?? "",
        country_id: kidData?.country_id ? String(kidData?.country_id) : "66",
        region_id: kidData?.region_id ? String(kidData?.region_id) : "",
        zone_id: kidData?.zone_id ? String(kidData?.zone_id) : "",
        city_id: kidData?.city_id ? String(kidData?.city_id) : "",
        subcity_id: kidData?.subcity_id ? String(kidData?.subcity_id) : "",
        woreda_id: kidData?.woreda_id ? String(kidData?.woreda_id) : "",
        kebele: kidData?.kebele ?? "",
        house_number: kidData?.house_number ?? "",
        current_state: kidData?.current_state ?? "",
        current_city: kidData?.current_city ?? "",
        current_zip_code: kidData?.current_zip_code ?? "",
        current_street_address: kidData?.current_street_address ?? "",
        current_building_number: kidData?.current_building_number ?? "",
        specific_location: kidData?.specific_location ?? ""
      });

      // Handle parents data after reset
      if (kidData?.parents?.length > 0) {
        // Set primary parent data from first parent
        const primaryParent = kidData.parents[0];
        setValue("primary_id", primaryParent.member_id ?? "");
        setValue("primary_relationship", primaryParent.relation_type ?? "");
        setValue("is_primary", "1");

        // Set secondary parent data if exists
        if (kidData.parents.length > 1) {
          const secondaryParent = kidData.parents[1];
          setValue("secondary_id", secondaryParent.member_id ?? "");
          setValue(
            "secondary_relationship",
            secondaryParent.relation_type ?? ""
          );
          setValue("is_secondary", "1");
        }
      }

      setKidData(kidData);
    }

    return () => {
      setKidData(null);
    };
  }, [kidQuery.data]);

  const onSubmit = data => {
    let cleanKidData = cloneDeep(data);

    const memberDataManipulator = new MemberDataManipulator();

    if (cleanKidData.date_of_birth) {
      cleanKidData.date_of_birth = formatISO(
        new Date(cleanKidData.date_of_birth),
        {
          representation: "date"
        }
      );
    }

    if (!isEditMode) {
      memberDataManipulator.removeEmptyValues(cleanKidData);
    }

    memberDataManipulator.convertValuesToNumbers(cleanKidData, [
      "is_primary",
      "is_secondary"
    ]);

    const parents = [];

    // Handle primary parent
    if (String(cleanKidData.is_primary) === "1") {
      parents.push({
        member_id: cleanKidData.primary_id,
        relation_type: cleanKidData.primary_relationship
      });

      memberDataManipulator.removeFromObject(cleanKidData, [
        "primary_id",
        "primary_religion",
        "primary_attending_church"
      ]);
    }

    // Handle secondary parent
    if (String(cleanKidData.is_secondary) === "1") {
      parents.push({
        member_id: cleanKidData.secondary_id,
        relation_type: cleanKidData.secondary_relationship
      });

      memberDataManipulator.removeFromObject(cleanKidData, [
        "secondary_id",
        "secondary_religion",
        "secondary_attending_church"
      ]);
    }

    // Set parents array if there are any parents
    if (parents.length > 0) {
      cleanKidData.parents = parents;
      memberDataManipulator.formatArrayStructure(cleanKidData, ["parents"]);
    }

    const kidFormData =
      memberDataManipulator.convertObjectToFormData(cleanKidData);

    if (data.image_kid) {
      kidFormData.append("image_kid", data.image_kid);
    }

    kidFormData.append("is_kid", "1");

    if (isEditMode) {
      kidUpdateMutation.mutate({
        churchId: selectedChurch?.id,
        kidData: kidFormData,
        kidId: id
      });
    } else {
      kidCreateMutation.mutate({
        churchId: selectedChurch?.id,
        kidData: kidFormData
      });
    }
  };

  if (isEditMode && kidQuery.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DevTool control={control} />
      <KidPersonalProfileSection
        register={register}
        errors={errors}
        isEditMode={isEditMode}
        setValue={setValue}
        watch={watch}
        control={control}
      />
      <Divider sx={{ my: 4 }} />
      <KidFamilyInformationSection
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
      />
      <Divider sx={{ my: 4 }} />
      <KidAddressSection
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
      />
      <MutationError
        error={kidCreateMutation.error || kidUpdateMutation.error}
        sx={{ mt: 4 }}
      />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 4 }}
      >
        <Button
          color="neutral"
          size="large"
          variant="contained"
          disabled={kidCreateMutation.isLoading || kidUpdateMutation.isLoading}
          onClick={() => {
            reset();
            navigate("/members");
          }}
        >
          {t("form.common.cancel")}
        </Button>
        <Button
          type="submit"
          size="large"
          variant="contained"
          disabled={kidCreateMutation.isLoading || kidUpdateMutation.isLoading}
        >
          {t("form.common.submit")}
        </Button>
      </Stack>
    </form>
  );
}
